import type { AbstractSubmitStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { AbstractSubmitStep } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { getMonthlyDepositOptions } from "@component/HomeSavings/Calculator/domain";
import headerImage from "@img/calculator/embed/home_savings_small/header.png";
import { FormCheck, FormSelect, ProgressButton } from "@shared/ui";
import { formatText } from "@shared/ui/format";
import autobind from "autobind-decorator";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { CONFIG, DEFAULT_PARAMS } from "../../Calculator/data";

interface Props extends AbstractSubmitStepProps {
	isPartner?: boolean;
}

export interface StepFields {
	monthlyDeposit: number;
	focusOn: boolean;
}

export class Step1 extends AbstractSubmitStep<StepFields, Props> {
	protected initValues(): StepFields {
		const { monthlyDeposit, focusOn } = DEFAULT_PARAMS;

		return {
			monthlyDeposit,
			focusOn,
		};
	}
	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { submitState, isPartner = false } = this.props;
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Img src={headerImage} variant="top" />
				<Card.Body>
					<div className="font-weight-bold mt-2 text-center mb-0 w-100 text-primary text-uppercase display-2">
						Kamatbónusz
					</div>
					<div className="h4 offer-title text-center my-3 mb-4 w-100">
						<mark className="text-nowrap">29 000 Ft</mark>-tól{" "}
						<mark className="text-nowrap">4 342 500 Ft</mark>-ig{" "}
					</div>
					<FormSelect
						className="pt-2"
						label="Mennyit tudsz félretenni havonta?"
						name="monthlyDeposit"
						options={getMonthlyDepositOptions()}
						infoTooltip="Havonta ennyit fizetsz be megtakarításként - ez alapján kapod a kamatbónuszt is."
						validate={this.validMonthlyDeposit}
						disabled={disabled}
						required
					/>
					<FormCheck label="Lakáscélú kölcsönnel együtt" name="focusOn" disabled={disabled} />
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						className={!form.getState().valid ? "disabled" : undefined}
						state={submitState}
					>
						Lakástakarékok összehasonlítása
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validMonthlyDeposit(monthlyDeposit: number, values: Record<string, any>): string {
		if (isNaN(monthlyDeposit)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (monthlyDeposit < CONFIG.MIN_MONTHLY_SAVINGS) {
			return `A minimális havi megtakarítás ${formatText(CONFIG.MIN_MONTHLY_SAVINGS, "ft")}!`;
		}

		if (monthlyDeposit > CONFIG.MAX_MONTHLY_SAVINGS) {
			return `A maximális havi megtakarítás ${formatText(CONFIG.MAX_MONTHLY_SAVINGS, "ft")}!`;
		}

		if (monthlyDeposit > values.realEstateValue * 0.8) {
			return `A hitelösszeg nem lehet nagyobb az ingatlan értékének 80%-ánál ${formatText(
				values.realEstateValue * 0.8,
				"ft",
			)}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
